// components/Molecules/UserCard/UserCard.js
import React from 'react';
import PropTypes from 'prop-types';
import { Card, Avatar, Button, Link } from '../../Atoms';
import Meta from '../Meta/Meta';

/**
 * UserCard - User profile preview card
 * 
 * A placement-agnostic card representing a user/author entity.
 * Uses block pattern with Avatar instead of image.
 * 
 * Supports primary action (View Profile) and optional secondary action.
 * 
 * Usage:
 *   <UserCard user={{ name, username, bio, photo, url, location }} />
 *   <UserCard 
 *     user={{...}} 
 *     action={{ label: 'Follow', onClick: fn }}
 *   />
 */

const UserCard = ({ user, action, className = '' }) => {
  const { name, username, bio, photo, url, location } = user || {};

  if (!name) {
    console.warn('UserCard: Missing required name');
    return null;
  }

  const metaItems = [];
  if (location) metaItems.push({ key: 'Location', value: location });

  return (
    <Card usage="interactive" className={`card-primary ${className}`}>
      <div className="block">
        <div className="block__header">
          <div className="flex justify-center">
            <Avatar imageUrl={photo} userName={name} size="xl" />
          </div>
        </div>
        <div className="block__body">
          <div className="stack stack-tight text-center">
            <div className="text-item-title">{name}</div>
            {username && <div className="text-label text-neutralMedium">@{username}</div>}
            {bio && <div className="text-body line-clamp-3">{bio}</div>}
            {metaItems.length > 0 && (
              <div className="flex justify-center">
                <Meta items={metaItems} />
              </div>
            )}
          </div>
        </div>
        {(url || action) && (
          <div className="block__footer">
            <div className="cluster cluster-normal cluster-center">
              {url && (
                <Link href={url} className="btn btn-primary">
                  View Profile
                </Link>
              )}
              {action && (
                <button
                  className="btn btn-secondary"
                  onClick={action.onClick}
                >
                  {action.label}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

UserCard.propTypes = {
  user: PropTypes.shape({
    name: PropTypes.string.isRequired,
    username: PropTypes.string,
    bio: PropTypes.string,
    photo: PropTypes.string,
    url: PropTypes.string,
    location: PropTypes.string,
  }).isRequired,
  action: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func,
  }),
  className: PropTypes.string,
};

export default UserCard;